import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import CalendarHeader from './CalendarHeader';
import DayView from './DayView';
import WeekView from './WeekView';
import MonthView from './MonthView';
import AppointmentDetail from './AppointmentDetail';
import NewAppointmentModal from './NewAppointmentModal';
import UpcomingList from './UpcomingList';
import { api } from '../../lib/api';
import { useOrg } from '../../hooks/useOrg';
import { useLocations } from '../../hooks/useLocations';
import { useAppointmentTypes } from '../../hooks/useAppointmentTypes';
import { Calendar, CheckCircle2, Clock } from 'lucide-react';

// Transform API appointment to the format existing components expect
function transformAppointment(appt) {
  return {
    id: appt.id,
    type: appt.type,
    client: appt.client ? {
      id: appt.client.id,
      name: `${appt.client.first_name} ${appt.client.last_name}`,
      caseNumber: appt.case_id || '',
      injury: '',
    } : { id: null, name: 'No client', caseNumber: '', injury: '' },
    staff: appt.provider ? {
      id: appt.provider.id,
      name: `${appt.provider.first_name} ${appt.provider.last_name}`,
      role: 'medical',
      avatar: `${(appt.provider.first_name?.[0] || '')}${(appt.provider.last_name?.[0] || '')}`,
      specialty: '',
    } : { id: null, name: 'Unassigned', role: 'medical', avatar: '??', specialty: '' },
    start: new Date(appt.starts_at),
    end: new Date(appt.ends_at),
    status: appt.status,
    location: appt.location_name || '',
    notes: appt.description || appt.outcome_notes || '',
    is_collaborator: appt.is_collaborator || false,
    owning_org: appt.owning_org || null,
    collaborator_info: appt.collaborator_info || [],
    // Keep raw data for updates
    _raw: appt,
  };
}

export default function Scheduler() {
  const { currentOrg } = useOrg();
  const { apiLocationFilter, currentLocationId, isAllLocations, locations } = useLocations();
  const { types, typesMap, loading: typesLoading, refetch: refetchTypes } = useAppointmentTypes();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'day' : 'week')); // a phone can't fit seven columns
  const [filters, setFilters] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPartnerAppts, setShowPartnerAppts] = useState(true);

  // Init filters when types load
  useEffect(() => {
    if (types.length && filters.length === 0) {
      setFilters(types.map(t => t.key));
    }
  }, [types, filters.length]);

  // Fetch appointments from API
  const fetchAppointments = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const params = apiLocationFilter ? { location_id: apiLocationFilter } : {};
      const data = await api.getAppointments(currentOrg.id, params);
      setAppointments(data.map(transformAppointment));
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, apiLocationFilter]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const filteredAppointments = useMemo(() => {
    const knownTypes = new Set(types.map(t => t.key));
    return appointments.filter(a => {
      // Partner appointments: controlled by the partner toggle only
      if (a.is_collaborator) return showPartnerAppts;
      // If the appointment's type isn't one of the org's configured
      // types (e.g. patient self-bookings default to 'consultation'),
      // always show it — otherwise it'd be invisible with no way to
      // toggle a chip for it.
      if (!knownTypes.has(a.type)) return true;
      return filters.includes(a.type);
    });
  }, [appointments, filters, showPartnerAppts, types]);

  const toggleFilter = (key) => {
    setFilters(prev =>
      prev.includes(key) ? prev.filter(f => f !== key) : [...prev, key]
    );
  };

  const handleStatusChange = async (id, status) => {
    if (!currentOrg) return;
    try {
      await api.updateAppointment(currentOrg.id, id, { status });
      setAppointments(prev =>
        prev.map(a => (a.id === id ? { ...a, status } : a))
      );
      setSelectedAppointment(prev => (prev && prev.id === id ? { ...prev, status } : prev));
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleNewAppointment = async (formData) => {
    if (!currentOrg) return;
    if (!formData.date || !formData.startTime || !formData.endTime) {
      alert('Please fill in date, start time, and end time');
      return;
    }
    try {
      // When the user is on "All locations" we let the server fall back
      // to the org's primary location. Otherwise we attach the currently
      // selected location so the appointment shows up under it.
      const targetLocationId = isAllLocations
        ? (locations.find(l => l.is_primary)?.id || null)
        : (currentLocationId || null);

      const created = await api.createAppointment(currentOrg.id, {
        type: formData.type || 'consultation',
        client_id: formData.clientId || null,
        provider_id: formData.staffId || null,
        starts_at: formData._starts_at || `${formData.date}T${formData.startTime}:00`,
        ends_at: formData._ends_at || `${formData.date}T${formData.endTime}:00`,
        location_id: targetLocationId,
        location_name: formData.location || null,
        description: formData.notes || null,
        status: 'pending',
      });

      // Video visit: attach the telehealth room now, so the invite email
      // carries the join link and the visit appears in Video Visits → Today.
      if (formData.videoVisit) {
        try {
          await api.createTelehealthSession(currentOrg.id, { appointment_id: created.id });
        } catch (e) {
          alert('Appointment saved, but the video room could not be created: ' + (e.message || e));
        }
      }

      // Add collaborators if any
      if (formData.collaborators?.length) {
        for (const collab of formData.collaborators) {
          try {
            await api.addCollaborator(currentOrg.id, created.id, {
              org_id: collab.org_id,
              user_id: collab.user_id || null,
              role_label: collab.role_label || null,
            });
          } catch (err) {
            console.error('Failed to add collaborator:', err);
          }
        }
      }

      setAppointments(prev => [...prev, transformAppointment(created)]);
    } catch (err) {
      console.error('Failed to create appointment:', err);
      alert(err.message);
    }
  };

  const stats = useMemo(() => {
    const today = new Date();
    const todayAppts = appointments.filter(
      a => a.start.toDateString() === today.toDateString()
    );
    return {
      total: todayAppts.length,
      confirmed: todayAppts.filter(a => a.status === 'confirmed').length,
      pending: todayAppts.filter(a => a.status === 'pending').length,
    };
  }, [appointments]);

  if (loading || typesLoading) {
    return (
      <div className="flex flex-1 min-h-0 bg-primary">
        {/* Skeleton sidebar */}
        <div className="hidden lg:block w-[272px] bg-secondary border-r border-theme p-5 flex-shrink-0">
          <div className="h-4 w-24 bg-tertiary rounded mb-4 animate-pulse" />
          <div className="grid grid-cols-7 gap-1 bg-tertiary p-2.5 rounded-[10px]">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="aspect-square rounded-lg bg-hover-surface animate-pulse" />
            ))}
          </div>
          <div className="mt-8 space-y-3">
            <div className="h-3 w-28 bg-tertiary rounded animate-pulse" />
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 px-2">
                <div className="w-4 h-4 rounded bg-tertiary animate-pulse" />
                <div className="w-2.5 h-2.5 rounded-full bg-tertiary animate-pulse" />
                <div className="h-3 flex-1 bg-tertiary rounded animate-pulse" style={{ maxWidth: `${80 + Math.random() * 60}px` }} />
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton main area */}
        <div className="flex-1 flex flex-col">
          {/* Stats skeleton */}
          <div className="flex items-center justify-between px-4 md:px-7 py-4 border-b border-theme">
            <div className="flex gap-3.5">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3 px-4 py-3 bg-elevated rounded-2xl border border-theme min-w-[120px]">
                  <div className="w-10 h-10 rounded-[10px] bg-tertiary animate-pulse" />
                  <div className="space-y-1.5">
                    <div className="h-5 w-8 bg-tertiary rounded animate-pulse" />
                    <div className="h-2.5 w-14 bg-tertiary rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden sm:block h-10 w-40 bg-tertiary rounded-[10px] animate-pulse" />
          </div>

          {/* Header skeleton */}
          <div className="flex items-center justify-between px-4 md:px-7 py-3 border-b border-theme">
            <div className="h-6 w-40 bg-tertiary rounded animate-pulse" />
            <div className="flex gap-1">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-8 w-16 bg-tertiary rounded-lg animate-pulse" />
              ))}
            </div>
          </div>

          {/* Calendar grid skeleton */}
          <div className="flex-1 p-4 md:p-7">
            <div className="grid grid-cols-7 gap-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-3 bg-tertiary rounded animate-pulse mx-auto w-8" />
              ))}
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="h-20 bg-tertiary rounded-lg animate-pulse opacity-60" style={{ animationDelay: `${i * 30}ms` }} />
              ))}
            </div>
          </div>
        </div>

        {/* Skeleton upcoming */}
        <div className="hidden xl:block w-[296px] bg-secondary border-l border-theme p-5 flex-shrink-0">
          <div className="h-3 w-16 bg-tertiary rounded mb-4 animate-pulse" />
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="p-3 rounded-[10px] bg-tertiary border border-theme animate-pulse" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="flex justify-between mb-2">
                  <div className="h-2.5 w-12 bg-hover-surface rounded" />
                  <div className="h-2.5 w-14 bg-hover-surface rounded" />
                </div>
                <div className="h-4 w-32 bg-hover-surface rounded mb-1.5" />
                <div className="flex justify-between">
                  <div className="h-2.5 w-20 bg-hover-surface rounded" />
                  <div className="h-2.5 w-16 bg-hover-surface rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 min-h-0 overflow-hidden">
      <Sidebar
        selectedDate={selectedDate}
        onDateSelect={setSelectedDate}
        filters={filters}
        onFilterChange={toggleFilter}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        types={types}
        onTypesChanged={refetchTypes}
        showPartnerAppts={showPartnerAppts}
        onTogglePartnerAppts={() => setShowPartnerAppts(prev => !prev)}
      />

      <main className="flex-1 flex flex-col overflow-hidden min-w-0 bg-primary">
        {/* Stats bar — compact on mobile */}
        <div className="flex items-center justify-between px-3 md:px-7 py-2.5 md:py-4 border-b border-theme bg-gradient-to-br from-[rgba(13, 167, 202,0.04)] to-[rgba(34, 51, 104,0.02)]">
          <div className="flex gap-2 md:gap-3.5 overflow-x-auto scrollbar-none">
            {[
              { n: stats.total, label: "Today's", Icon: Calendar, cls: 'bg-accent-glow text-accent-light' },
              { n: stats.confirmed, label: 'Confirmed', Icon: CheckCircle2, cls: 'bg-success-soft text-success' },
              { n: stats.pending, label: 'Pending', Icon: Clock, cls: 'bg-warning-soft text-warning' },
            ].map(s => (
              <div key={s.label} className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2 md:py-3 bg-elevated rounded-xl md:rounded-2xl border border-theme shadow-sm flex-shrink-0">
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-[8px] md:rounded-[10px] flex items-center justify-center flex-shrink-0 ${s.cls}`}>
                  <s.Icon size={18} strokeWidth={2} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[18px] md:text-[24px] font-bold leading-none tracking-tight">{s.n}</span>
                  <span className="text-[10px] md:text-[11px] text-muted-c font-medium mt-0.5">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="hidden sm:flex items-center gap-2 px-5 py-[11px] gradient-accent text-white rounded-[10px] text-[13px] font-semibold shadow-glow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all flex-shrink-0 ml-4"
          >
            <span className="text-lg leading-none">+</span> New Appointment
          </button>
        </div>

        {/* Mobile FAB */}
        <button
          onClick={() => setShowNewModal(true)}
          className="sm:hidden fixed bottom-10 right-5 z-30 w-14 h-14 rounded-full gradient-accent text-white text-2xl flex items-center justify-center shadow-glow-lg hover:scale-105 transition-transform"
        >
          +
        </button>

        <CalendarHeader
          selectedDate={selectedDate}
          onDateSelect={setSelectedDate}
          view={view}
          onViewChange={setView}
          onToggleSidebar={() => setSidebarOpen(true)}
        />

        <div className="flex-1 overflow-y-auto">
          {view === 'day' && (
            <DayView date={selectedDate} appointments={filteredAppointments} onSelectAppointment={setSelectedAppointment} typesMap={typesMap} />
          )}
          {view === 'week' && (
            <WeekView date={selectedDate} appointments={filteredAppointments} onSelectAppointment={setSelectedAppointment} typesMap={typesMap} />
          )}
          {view === 'month' && (
            <MonthView
              date={selectedDate}
              appointments={filteredAppointments}
              onSelectAppointment={setSelectedAppointment}
              onDateSelect={setSelectedDate}
              onViewChange={setView}
              typesMap={typesMap}
            />
          )}
        </div>
      </main>

      <UpcomingList appointments={filteredAppointments} onSelectAppointment={setSelectedAppointment} typesMap={typesMap} />

      {selectedAppointment && (
        <AppointmentDetail
          appointment={selectedAppointment}
          onClose={() => setSelectedAppointment(null)}
          onStatusChange={handleStatusChange}
          typesMap={typesMap}
          onDelete={(id) => {
            setAppointments(prev => prev.filter(a => a.id !== id));
            setSelectedAppointment(null);
          }}
          onUpdate={(id, updates) => {
            setSelectedAppointment(prev => (prev && prev.id === id ? { ...prev, ...updates } : prev));
            setAppointments(prev => prev.map(a =>
              a.id === id ? { ...a, ...updates } : a
            ));
            setSelectedAppointment(prev =>
              prev && prev.id === id ? { ...prev, ...updates } : prev
            );
          }}
        />
      )}
      {showNewModal && (
        <NewAppointmentModal
          onClose={() => setShowNewModal(false)}
          onSave={handleNewAppointment}
          types={types}
        />
      )}
    </div>
  );
}
